import React from 'react';
import {
  Box, Typography, FormControlLabel, Checkbox,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Package, Thermometer, DoorOpen, Bus, Mail, KeyRound, PenLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const SpecialOptionsForm = ({
  returnParcels,
  setReturnParcels,
  returnMethod,
  setReturnMethod,
  temperatureControl,
  setTemperatureControl,
  canBeLeftAtDoor,
  setCanBeLeftAtDoor,
  requiresPin,
  setRequiresPin,
  requiresSignature,
  setRequiresSignature,
}) => {
  const { t } = useTranslation();

  const options = [
    {
      key: 'canBeLeftAtDoor',
      value: canBeLeftAtDoor,
      onChange: (v) => setCanBeLeftAtDoor?.(v),
      icon: <DoorOpen size={18} />,
      label: t('delivery.canBeLeftAtDoor') || 'Can Be Left at Door',
      description: t('delivery.canBeLeftAtDoorDesc') || 'Package can be left without signature',
    },
    {
      key: 'returnParcels',
      value: returnParcels,
      onChange: (v) => setReturnParcels?.(v),
      icon: <Package size={18} />,
      label: t('delivery.returnParcels') || 'Return Parcels',
      description: t('delivery.returnParcelsDesc') || 'Driver will collect return items',
    },
    {
      key: 'temperatureControl',
      value: temperatureControl,
      onChange: (v) => setTemperatureControl?.(v),
      icon: <Thermometer size={18} />,
      label: t('delivery.temperatureControl') || 'Temperature Control',
      description: t('delivery.temperatureControlDesc') || 'Requires temperature-controlled handling',
    },
    {
      key: 'requiresPin',
      value: requiresPin,
      onChange: (v) => {
        setRequiresPin?.(v);
        if (v) setRequiresSignature?.(false); // mutually exclusive
      },
      icon: <KeyRound size={18} />,
      label:t('delivery.requiresPin'), // t('delivery.requiresPin') || 'Requires PIN',
      description:t('delivery.requiresPinDesc'), // t('delivery.requiresPinDesc') || 'Recipient must provide PIN to receive',
    },
    {
      key: 'requiresSignature',
      value: requiresSignature,
      onChange: (v) => {
        setRequiresSignature?.(v);
        if (v) setRequiresPin?.(false); // mutually exclusive
      },
      icon: <PenLine size={18} />,
      label: t('delivery.requiresSignature'), // t('delivery.requiresSignature') || 'Requires Signature',
      description: t('delivery.requiresSignatureDesc'), // t('delivery.requiresSignatureDesc') || 'Recipient must sign to receive',
    },
  ];

  return (
    <Box>
      {options.map((option, index) => (
        <Box key={option.key}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!option.value}
                onChange={(e) => option.onChange(e.target.checked)}
                sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {option.icon}
                <Box>
                  <Typography variant="body2" fontWeight={500}>{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                </Box>
              </Box>
            }
            sx={{
              width: '100%', m: 0, py: 1.5, px: 0,
              borderRadius: 1, '&:hover': { bgcolor: 'action.hover' },
            }}
          />

          {/* Return method inline */}
          {option.key === 'returnParcels' && returnParcels && (
            <Box sx={{ ml: 4, mb: 1.5, mt: -0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                {t('delivery.returnMethod') || 'Return method'}
              </Typography>
              <ToggleButtonGroup
                value={returnMethod}
                exclusive
                onChange={(_, val) => { if (val) setReturnMethod?.(val); }}
                size="small"
              >
                <ToggleButton value="bus" sx={{ gap: 0.75, px: 2 }}>
                  <Bus size={15} />
                  <Typography variant="caption" fontWeight={500}>
                    {t('delivery.returnByBus') || 'By bus'}
                  </Typography>
                </ToggleButton>
                <ToggleButton value="posti" sx={{ gap: 0.75, px: 2 }}>
                  <Mail size={15} />
                  <Typography variant="caption" fontWeight={500}>
                    {t('delivery.returnByPost') || 'By Posti'}
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {index < options.length - 1 && (
            <Box sx={{ height: 1, bgcolor: 'divider', my: 0.5 }} />
          )}
        </Box>
      ))}
    </Box>
  );
};